import React, { useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useNavigate } from 'react-router';
import { useAppContext } from './AppContext';
import SConsciousApiService from '../services/SConsciousApiService';

const CATEGORIES = [
    'Loan',
    'Utility',
    'Rent',
    'Insurance',
    'Subscription',
    'Transport',
    'Groceries',
    'Healthcare',
    'Education',
    'Entertainment',
    'Dining',
    'Other',
];

const RECURRENCE_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
];

const _api = new SConsciousApiService();

const ManualReceipt: React.FC = () => {
    const navigate = useNavigate();
    const { appData, setAppData } = useAppContext();

    const [merchantName, setMerchantName] = useState('');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [category, setCategory] = useState('');
    const [recurring, setRecurring] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSave = merchantName.trim() && amount && parseFloat(amount) > 0 && date && category;

    const handleSave = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        setError(null);

        try {
            const result = await _api.createManualReceipt(
                appData.user.userId,
                merchantName.trim(),
                parseFloat(amount),
                appData.user.currency || 'EUR',
                date,
                category,
                recurring || undefined,
                description.trim() || undefined,
            );

            if (result?.receipt_id) {
                // Trigger data refresh so the new receipt shows in the list
                setAppData({ ...appData, loading: true });
                navigate(-1);
            } else {
                setError(result?.error || 'Failed to create receipt');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '24px 16px', maxWidth: 480, margin: '0 auto' }}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>New Receipt</span>
                <button
                    className="closeBtn"
                    onClick={() => navigate(-1)}
                    aria-label="Close"
                    style={{ fontFamily: 'inherit', background: '#000', color: '#ffcc00', width: 32, height: 32, fontSize: '0.85rem', boxShadow: 'none' }}
                >
                    <FontIcon iconName="Cancel" />
                </button>
            </div>

            {/* Merchant name */}
            <label style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' }}>Merchant</label>
            <input
                className="darkInput"
                placeholder="e.g. Landlord, Electric Company"
                value={merchantName}
                onChange={e => setMerchantName(e.target.value)}
                style={{ marginBottom: 16 }}
            />

            {/* Amount */}
            <label style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' }}>Amount ({appData.user.currency || 'EUR'})</label>
            <input
                className="darkInput"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={{ marginBottom: 16 }}
            />

            {/* Date */}
            <label style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' }}>Date</label>
            <input
                className="darkInput"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ marginBottom: 16 }}
            />

            {/* Category */}
            <label style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' }}>Category</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {CATEGORIES.map(cat => (
                    <button
                        key={cat}
                        className="actionButton"
                        onClick={() => setCategory(cat)}
                        style={{
                            fontFamily: 'inherit',
                            background: category === cat ? '#000' : 'rgba(0,0,0,0.08)',
                            color: category === cat ? '#ffcc00' : '#000',
                            border: 'none',
                            fontSize: '0.8rem',
                            padding: '0.3rem 0.7rem',
                            cursor: 'pointer',
                        }}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Recurring */}
            <label style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' }}>Recurring</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {RECURRENCE_OPTIONS.map(opt => (
                    <button
                        key={opt.value}
                        className="actionButton"
                        onClick={() => setRecurring(opt.value)}
                        style={{
                            fontFamily: 'inherit',
                            background: recurring === opt.value ? '#000' : 'rgba(0,0,0,0.08)',
                            color: recurring === opt.value ? '#ffcc00' : '#000',
                            border: 'none',
                            fontSize: '0.8rem',
                            padding: '0.3rem 0.7rem',
                            cursor: 'pointer',
                        }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Description (optional) */}
            <label style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' }}>Description (optional)</label>
            <input
                className="darkInput"
                placeholder="e.g. April rent payment"
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{ marginBottom: 24 }}
            />

            {/* Error */}
            {error && <div style={{ color: 'red', fontSize: '0.82rem', marginBottom: 12 }}>{error}</div>}

            {/* Save button */}
            <button
                className="actionButton"
                onClick={handleSave}
                disabled={!canSave || saving}
                style={{
                    fontFamily: 'inherit',
                    width: '100%',
                    opacity: canSave && !saving ? 1 : 0.5,
                    cursor: canSave && !saving ? 'pointer' : 'default',
                }}
            >
                {saving ? 'Saving…' : 'Create Receipt'}
            </button>
        </div>
    );
};

export default ManualReceipt;
